import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { Box, Chip } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ETheme } from "../../../shared/enums/user/ETheme";
import { formatDate } from "../../../shared/functions/formatDate";
import { getInvoiceStatusColor } from "../../../shared/functions/getChipColors";
import { EditIcon, TrashIcon } from "../../../shared/icons/Icons";
import { IInvoice } from "../../../shared/interfaces/invoice/IInvoice";
import { darkTheme, lightTheme } from "../../../shared/mui/theme";
import { RootState } from "../../../store/store";

export const useInvoiceColumns = (
  setSelectedInvoice: (project: IInvoice) => void,
  setShowDeleteModal: (show: boolean) => void,
  hideActionColumn = false,
): GridColDef[] => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useSelector((state: RootState) => state.auth);
  return [
    { field: "id", headerName: t("general.id"), width: 80 },
    {
      field: "invoiceNumber",
      headerName: t("invoice.invoiceNumber"),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div
          style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          onClick={() => navigate(`/invoices/preview/${params.row?.id}`)}
        >
          {params.value}
        </div>
      ),
    },
    {
      field: "client",
      headerName: t("general.client"),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => params?.row?.client?.name ?? "-",
    },
    {
      field: "status",
      headerName: t("general.status"),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Chip
          label={t(`invoice.status.${params.value}`)}
          color={getInvoiceStatusColor(params.value)}
          variant="outlined"
        />
      ),
    },
    {
      field: "dueDate",
      headerName: t("invoice.dueDate"),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          {formatDate(params.value)}
        </div>
      ),
    },
    {
      field: "actions",
      headerName: t("general.action"),
      width: 150,
      sortable: false,
      headerClassName: hideActionColumn ? "hideColumn" : "",
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Box
          sx={{
            display: hideActionColumn ? "none" : "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          {/* Edit Icon */}
          <span
            style={{ cursor: "pointer", display: "flex" }}
            onClick={() => navigate(`/invoice/update/${params.row?.id}`)}
          >
            <EditIcon
              fill={
                userData?.theme === ETheme.LIGHT
                  ? lightTheme.palette.text.primary
                  : darkTheme.palette.text.primary
              }
            />
          </span>

          {/* Delete Icon */}
          <span
            style={{ cursor: "pointer" }}
            className={"mx-3 d-flex"}
            onClick={() => {
              setSelectedInvoice(params.row);
              setShowDeleteModal(true);
            }}
          >
            <TrashIcon
              fill={
                userData?.theme === ETheme.LIGHT
                  ? lightTheme.palette.text.primary
                  : darkTheme.palette.text.primary
              }
            />
          </span>

          {/* View Icon */}
          <span
            style={{ cursor: "pointer", display: "flex" }}
            onClick={() => navigate(`/invoices/preview/${params.row?.id}`)}
          >
            <RemoveRedEyeOutlinedIcon
              fill={
                userData?.theme === ETheme.LIGHT
                  ? lightTheme.palette.text.primary
                  : darkTheme.palette.text.primary
              }
            />
          </span>
        </Box>
      ),
    },
  ];
};
